/**
 * Recurrence Serialization Service
 *
 * Handles serialization and deserialization of recurrence configuration
 * between the editor format and workflow JSON format.
 */

import type { RecurrenceValue, RecurrenceSchedule, Frequency, WeekDay } from '@/types/recurrence';
import {
  DEFAULT_RECURRENCE,
  FrequencyValues,
  WeekDayValues,
  RecurrenceValueSchema,
} from '@/types/recurrence';
import type { ValueSegment } from '@/types/valueSegment';

// =============================================================================
// WORKFLOW JSON FORMAT
// =============================================================================

/**
 * Recurrence format in workflow.json
 */
export interface WorkflowRecurrence {
  frequency: string;
  interval: number;
  startTime?: string;
  timeZone?: string;
  schedule?: {
    hours?: number[];
    minutes?: number[];
    weekDays?: string[];
    monthDays?: number[];
  };
}

// =============================================================================
// SERIALIZATION
// =============================================================================

/**
 * Serialize RecurrenceValue to workflow JSON format
 */
export function serializeRecurrence(value: RecurrenceValue): WorkflowRecurrence {
  const result: WorkflowRecurrence = {
    frequency: value.frequency,
    interval: value.interval,
  };

  if (value.startTime) {
    result.startTime = value.startTime;
  }

  if (value.timeZone) {
    result.timeZone = value.timeZone;
  }

  if (value.schedule) {
    const schedule: WorkflowRecurrence['schedule'] = {};
    let hasSchedule = false;

    if (value.schedule.hours && value.schedule.hours.length > 0) {
      schedule.hours = value.schedule.hours;
      hasSchedule = true;
    }

    if (value.schedule.minutes && value.schedule.minutes.length > 0) {
      schedule.minutes = value.schedule.minutes;
      hasSchedule = true;
    }

    if (value.schedule.weekDays && value.schedule.weekDays.length > 0) {
      schedule.weekDays = value.schedule.weekDays;
      hasSchedule = true;
    }

    if (value.schedule.monthDays && value.schedule.monthDays.length > 0) {
      schedule.monthDays = value.schedule.monthDays;
      hasSchedule = true;
    }

    if (hasSchedule) {
      result.schedule = schedule;
    }
  }

  return result;
}

// =============================================================================
// DESERIALIZATION
// =============================================================================

/**
 * Deserialize workflow JSON to RecurrenceValue
 */
export function deserializeRecurrence(json: WorkflowRecurrence | undefined): RecurrenceValue {
  if (!json) {
    return { ...DEFAULT_RECURRENCE };
  }

  // Validate and parse frequency
  const frequency = parseFrequency(json.frequency);

  // Validate interval
  const interval = typeof json.interval === 'number' && json.interval >= 1 ? json.interval : 1;

  const value: RecurrenceValue = {
    frequency,
    interval,
  };

  if (json.startTime && typeof json.startTime === 'string') {
    value.startTime = json.startTime;
  }

  if (json.timeZone && typeof json.timeZone === 'string') {
    value.timeZone = json.timeZone;
  }

  if (json.schedule) {
    const schedule: RecurrenceSchedule = {};
    let hasSchedule = false;

    if (Array.isArray(json.schedule.hours)) {
      schedule.hours = json.schedule.hours.filter((h) => typeof h === 'number' && h >= 0 && h <= 23);
      if (schedule.hours.length > 0) hasSchedule = true;
    }

    if (Array.isArray(json.schedule.minutes)) {
      schedule.minutes = json.schedule.minutes.filter(
        (m) => typeof m === 'number' && m >= 0 && m <= 59
      );
      if (schedule.minutes.length > 0) hasSchedule = true;
    }

    if (Array.isArray(json.schedule.weekDays)) {
      schedule.weekDays = json.schedule.weekDays.filter((d): d is WeekDay =>
        WeekDayValues.includes(d as WeekDay)
      );
      if (schedule.weekDays.length > 0) hasSchedule = true;
    }

    if (Array.isArray(json.schedule.monthDays)) {
      schedule.monthDays = json.schedule.monthDays.filter(
        (d) => typeof d === 'number' && d >= -1 && d <= 31
      );
      if (schedule.monthDays.length > 0) hasSchedule = true;
    }

    if (hasSchedule) {
      value.schedule = schedule;
    }
  }

  return value;
}

/**
 * Parse frequency string to typed Frequency
 */
function parseFrequency(value: string | undefined): Frequency {
  if (!value) return 'Day';

  // Case-insensitive match
  const normalized = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  if (FrequencyValues.includes(normalized as Frequency)) {
    return normalized as Frequency;
  }

  return 'Day';
}

// =============================================================================
// VALUE SEGMENT CONVERSION
// =============================================================================

/**
 * Convert RecurrenceValue to ValueSegment array for storage
 */
export function recurrenceToSegments(value: RecurrenceValue): ValueSegment[] {
  const serialized = JSON.stringify(value);
  return [
    {
      id: `seg_recurrence_${Date.now()}`,
      type: 'literal',
      value: serialized,
    },
  ];
}

/**
 * Convert ValueSegment array back to RecurrenceValue
 */
export function segmentsToRecurrence(segments: ValueSegment[]): RecurrenceValue {
  if (!segments || segments.length === 0) {
    return { ...DEFAULT_RECURRENCE };
  }

  // Combine all literal segments
  const combined = segments
    .filter((s) => s.type === 'literal')
    .map((s) => s.value)
    .join('');

  if (!combined) {
    return { ...DEFAULT_RECURRENCE };
  }

  try {
    const parsed = JSON.parse(combined);
    const result = RecurrenceValueSchema.safeParse(parsed);
    if (result.success) {
      // Build result manually to satisfy exactOptionalPropertyTypes
      const data = result.data;
      const value: RecurrenceValue = {
        frequency: data.frequency,
        interval: data.interval,
      };
      if (data.startTime !== undefined) {
        value.startTime = data.startTime;
      }
      if (data.timeZone !== undefined) {
        value.timeZone = data.timeZone;
      }
      if (data.schedule !== undefined) {
        // Build schedule manually to satisfy exactOptionalPropertyTypes
        const schedule: RecurrenceSchedule = {};
        if (data.schedule.hours !== undefined) {
          schedule.hours = data.schedule.hours;
        }
        if (data.schedule.minutes !== undefined) {
          schedule.minutes = data.schedule.minutes;
        }
        if (data.schedule.weekDays !== undefined) {
          schedule.weekDays = data.schedule.weekDays;
        }
        if (data.schedule.monthDays !== undefined) {
          schedule.monthDays = data.schedule.monthDays;
        }
        if (Object.keys(schedule).length > 0) {
          value.schedule = schedule;
        }
      }
      return value;
    }
    // If Zod validation fails, try manual deserialization
    return deserializeRecurrence(parsed);
  } catch {
    return { ...DEFAULT_RECURRENCE };
  }
}

// =============================================================================
// VALIDATION
// =============================================================================

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validate recurrence configuration
 */
export function validateRecurrence(value: RecurrenceValue): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate interval
  if (value.interval < 1) {
    errors.push({ field: 'interval', message: 'Interval must be at least 1' });
  }
  if (value.interval > 1000) {
    errors.push({ field: 'interval', message: 'Interval cannot exceed 1000' });
  }

  // Validate schedule combinations
  if (value.schedule) {
    // WeekDays only valid for Week frequency
    if (value.schedule.weekDays && value.schedule.weekDays.length > 0) {
      if (value.frequency !== 'Week') {
        errors.push({
          field: 'schedule.weekDays',
          message: 'Week days can only be specified with Week frequency',
        });
      }
    }

    // MonthDays only valid for Month frequency
    if (value.schedule.monthDays && value.schedule.monthDays.length > 0) {
      if (value.frequency !== 'Month') {
        errors.push({
          field: 'schedule.monthDays',
          message: 'Month days can only be specified with Month frequency',
        });
      }
    }

    // Hours/minutes only valid for Day, Week, Month
    if (value.schedule.hours || value.schedule.minutes) {
      if (value.frequency === 'Hour' || value.frequency === 'Minute' || value.frequency === 'Second') {
        errors.push({
          field: 'schedule.hours',
          message: 'Schedule hours/minutes can only be specified with Day, Week, or Month frequency',
        });
      }
    }
  }

  // Validate startTime format
  if (value.startTime) {
    const date = new Date(value.startTime);
    if (isNaN(date.getTime())) {
      errors.push({ field: 'startTime', message: 'Invalid start time format' });
    }
  }

  return errors;
}

/**
 * Check if recurrence is valid
 */
export function isValidRecurrence(value: RecurrenceValue): boolean {
  return validateRecurrence(value).length === 0;
}
